import { Slide } from '@progress/kendo-react-animation';
import { Loader } from '@progress/kendo-react-indicators';
import { Notification, NotificationProps } from '@progress/kendo-react-notification';
import React from 'react';
import { NavLink, useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks/store-hooks';
import { ROUTES } from '../../app/router/routes';
import { dismissNotification, NotificationStateItem, NotificationType } from '../../app/state/slices/global-slice';

export default function NotificationsList() {
    const notifications = useAppSelector((x) => x.global.notifications);
    const dispatch = useAppDispatch();
    const handleDismissNotification = React.useCallback(
        (id: string) => {
            dispatch(dismissNotification({ id }));
        },
        [dispatch]
    );

    return (
        <div>
            <Slide
                className={'z-3 k-notification-group'}
                style={{
                    right: 10,
                    bottom: 10,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap-reverse',
                    position: 'fixed'
                }}
                direction={'left'}>
                {notifications.map((x) => (
                    <Notification
                        {...toNotification(x)}
                        key={x.id}
                        closable={x.closable}
                        onClose={() => handleDismissNotification(x.id)}></Notification>
                ))}
            </Slide>
        </div>
    );
}

function toNotification(notification: NotificationStateItem): Pick<NotificationProps, 'type' | 'children'> {
    switch (notification.type) {
        case NotificationType.noRecordingDir:
            return {
                type: {
                    style: 'warning'
                },
                children: (
                    <span>
                        Can't start recording. Recording directory not set. Go to <NavLink to="/settings/general">settings</NavLink> to
                        change it
                    </span>
                )
            };
        case NotificationType.weatherRecordingDirError:
            return {
                type: {
                    style: 'warning'
                },
                children: <span>Failed to create weather recording directory.</span>
            };

        case NotificationType.noMyFleetFile:
            return {
                type: {
                    style: 'warning'
                },
                children: (
                    <span>
                        My fleet file not found. Go to <NavLink to="/settings/aircraft">settings</NavLink> to set it.
                    </span>
                )
            };

        case NotificationType.myFileFileParseError:
            return {
                type: {
                    style: 'warning'
                },
                children: (
                    <span>Could not parse or get access to my fleet file. Check if file exists and if it is formatted correctly.</span>
                )
            };

        case NotificationType.playbackFail:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not start the playback</span>
            };

        case NotificationType.imageSaveInProgress:
            return {
                type: {
                    style: 'none'
                },
                children: (
                    <span>
                        Saving map image <Loader type={'pulsing'} size={'small'} />
                    </span>
                )
            };

        case NotificationType.generalSuccess:
            return {
                type: {
                    style: 'success'
                },
                children: <span>{notification.customMessage ?? 'Success'}</span>
            };

        case NotificationType.generalError:
            return {
                type: {
                    style: 'error'
                },
                children: <span>{notification.customMessage ?? 'An error occurred'}</span>
            };

        case NotificationType.generalWarning:
            return {
                type: {
                    style: 'warning'
                },
                children: <span>{notification.customMessage ?? 'Warning'}</span>
            };
        case NotificationType.generalInfo:
            return {
                type: {
                    style: 'info'
                },
                children: <span>{notification.customMessage ?? 'Info'}</span>
            };
        case NotificationType.udViewSaveError:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not save view</span>
            };
        case NotificationType.filterGroupSaveError:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not save filter group</span>
            };
        case NotificationType.thresholdSaveError:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not save thresholds</span>
            };
        case NotificationType.emailAlertSendFail:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Sending email alert failed</span>
            };
        case NotificationType.emailAlertNoEmailAddress:
            return {
                type: {
                    style: 'warning'
                },
                children: <span>Could not send email alert, no destination email address found.</span>
            };
        case NotificationType.emailAlertTooManyRequest:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Send email alert too many requests</span>
            };
        case NotificationType.emailAlertGeneralError:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not send email alerts</span>
            };
        case NotificationType.routeDecodeError:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not decode route</span>
            };
        case NotificationType.playbackMissingWeatherDirectory:
            return {
                type: {
                    style: 'error'
                },
                children: <span>Could not find any recorded weather files</span>
            };
        case NotificationType.udViewsImportSuccess:
            return {
                type: {
                    style: 'success'
                },
                children: <span>{notification.customMessage}</span>
            };
        case NotificationType.udViewsImportFailed:
            return {
                type: {
                    style: 'error'
                },
                children: <span>{notification.customMessage}</span>
            };
        case NotificationType.integrationInProgress:
            return {
                type: {
                    style: 'info'
                },
                children: (
                    <span className="d-flex align-items-center gap-2">
                        {notification.customMessage} <Loader type={'pulsing'} size={'small'} />
                    </span>
                )
            };
        case NotificationType.integrationRoutesReceived:
            return {
                type: {
                    style: 'success'
                },
                children: <IntegrationRoutesReceived message={notification.customMessage} />
            };
    }

    return {};
}

function IntegrationRoutesReceived(props: { message?: string }) {
    const location = useLocation();
    return (
        <span>
            {props.message}
            {location.pathname !== ROUTES.HOME && (
                <>
                    {' '}
                    <NavLink to={ROUTES.HOME}>Show</NavLink>
                </>
            )}
        </span>
    );
}
